import initializeNotifications from './initializeNotifications';
import { initializeOpx } from './initializeOpx';
import { lazyInitializeExtensibilityContext } from 'owa-addins-core/lib/lazyFunctions';
import { initializeAppModulePostBoot } from 'owa-app-module-post-boot';
import { subscribeToNotifications } from 'owa-app-notifications-core';
import { lazyLoadCharmsCatalog } from 'owa-calendar-charms-store';
import { lazyInitCalendarCloudSettings } from 'owa-calendar-cloud-settings';
import { lazyGetCalendarModuleDiagnostics } from 'owa-calendar-diagnostics';
import { lazyInitializeEventsNow } from 'owa-calendar-events-now';
import { lazyPreloadFormBundles } from 'owa-calendar-form-preload';
import { lazyCalendarCommandingOrchestrationModule } from 'owa-calendar-module-commanding-orchestration';
import { lazyCalendarOrchestrationModule } from 'owa-calendar-module-orchestration';
import { registerModuleStitches } from 'owa-calendar-module-view/lib/utils/registerModuleStitches';
import { lazyInitializeEventRelatedContent } from 'owa-calendar-related-content-initialization';
import { lazyAutoUpgradeOldModelCalendars } from 'owa-calendar-sharing-autoupgrade';
import { CalendarViewTypeEnum } from 'owa-calendar-view-type';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyUpdateTenantConfigForFluid } from 'owa-fluid-tenantconfig';
import { lazyLoadUnifiedGroupsSettings } from 'owa-groups-shared-actions';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { lazyLoadSignature } from 'owa-mail-signature';
import { isInsightsEnabledForUser } from 'owa-meeting-insights-types-and-flights';
import { lazySetup } from 'owa-notification';
import { lazyLoadSavedCalendarViews } from 'owa-save-calendar-view-data';
import { getUserConfiguration, isConsumer } from 'owa-session-store';
import { lazyGovern } from 'owa-tti';
import { weatherPullTask } from 'owa-weather';

/**
 * Tasks that need to be initialized for calendar deeplink scenarios
 *
 * Consider task ordering and priority from a holistic perspective when making changes!
 */
export function setupCalendarDeeplinkFunctionalBoot() {
    initializeAppModulePostBoot(false /* isFullApp */);

    lazyGovern.importAndExecute(
        {
            task: () => lazyInitCalendarCloudSettings.importAndExecute(),
        },
        {
            task: () => lazyUpdateTenantConfigForFluid.importAndExecute(),
            condition: isFeatureEnabled('cal-cmp-fluid'),
        },
        {
            task: () => lazyLoadCharmsCatalog.importAndExecute(),
        }
    );
}

/**
 * Tasks that need to be initialized for calendar app scenarios
 *
 * Consider task ordering and priority from a holistic perspective when making changes!
 */
export function setupCalendarFunctionalBoot() {
    initializeAppModulePostBoot(true /* isFullApp */);

    lazyGovern.importAndExecute(
        // CORE SETUP TASKS
        {
            // Initialize the singleton diagnostics store to register the diagnostics listeners
            task: () => lazyGetCalendarModuleDiagnostics.importAndExecute(),
            condition: isFeatureEnabled('fwk-devTools'),
        },
        {
            task: () => lazySetup.importAndExecute(),
        },
        initializeNotifications,
        subscribeToNotifications,
        {
            task: () => lazyLoadSavedCalendarViews.importAndExecute(),
            condition: isFeatureEnabled('cal-saveCalendarView'),
        },
        // TODO: VSO #89735 Audit boundary between surface and board
        // This task should eventually be removed or else moved down as an idle task
        // for optimization purposes only, but more work is needed to do this safely
        // without incurring bundle size issues
        {
            task: () => lazyCalendarOrchestrationModule.import(),
        },
        registerModuleStitches,
        {
            task: initializeOpx,
            condition: isHostAppFeatureEnabled('surfacePLTDataPoint'),
        },
        // ADDITIONAL POST-RENDER TASKS
        {
            task: () => lazyLoadCharmsCatalog.importAndExecute(),
        },
        {
            task: () => lazyInitializeEventsNow.importAndExecute(),
        },
        weatherPullTask,
        {
            task: () => lazyInitCalendarCloudSettings.importAndExecute(),
        },
        {
            task: () => lazyUpdateTenantConfigForFluid.importAndExecute(),
            condition: isFeatureEnabled('cal-cmp-fluid'),
        },
        {
            task: () => lazyInitializeEventRelatedContent.importAndExecute(),
            condition: isInsightsEnabledForUser(),
        },
        {
            task: () => lazyLoadUnifiedGroupsSettings.importAndExecute(),
            condition: getUserConfiguration().GroupsEnabled,
        },
        // IDLE/OPTIMIZATION TASKS
        {
            task: () => lazyCalendarCommandingOrchestrationModule.import(),
            idle: true,
        },
        {
            task: () => lazyInitializeExtensibilityContext.importAndExecute(),
            idle: true,
        },
        {
            task: () => lazyPreloadFormBundles.importAndExecute(),
            condition: isFeatureEnabled('cal-mf-preloadForms'),
            idle: true,
        },
        {
            task: () => lazyAutoUpgradeOldModelCalendars.importAndExecute(),
            /**
             * Client-side hueristics to make the autoUpgrade decision of old model calendars
             *   1. Only for business users
             *   2. User is in day view
             *   3. User has split view turned on
             */
            condition:
                !isConsumer() &&
                getUserConfiguration()?.ViewStateConfiguration?.CalendarViewTypeDesktop ==
                    CalendarViewTypeEnum.Daily &&
                getUserConfiguration()?.ViewStateConfiguration?.CalendarViewInSplitMode,
            idle: true,
        },
        {
            // this should ideally move out of post-boot (or at least only remain as an optimization),
            // but leaving here for now given that the fix requires domain expertise
            //
            // all relevant scenarios like mail compose and signature option in settings should handle
            // this by default, and consumers should only ever handle load as an optional optimization
            task: () => lazyLoadSignature.importAndExecute(),
            idle: true,
        }
    );
}
